// Live-Gespräch-API (Live-Audio v2): REST-Start/-Ende + WS-URL-Bau.
// POST /start legt die Session (status='pending') + Einmal-Ticket (60 s TTL,
// Single-Use) an; die eigentliche Verbindung läuft über den WS-Relay-Pfad.
// Das Ticket reist als WebSocket-SUBPROTOCOL (new WebSocket(url, ticket)) —
// NICHT als Query-Param, weil Query-Strings in den Apache-Access-Logs landen.
// Alle REST-Aufrufe gehen über apiRequest (Bearer auf Native, /api-Prefix,
// res.ok-Check, Timeout).
import { ApiError, apiRequest } from '../../../api/client';

// Gesprächsstil (Live-Gespräch v3): kurzer Dialog vs. lange Erzählpassagen.
export type LiveTalkStyle = 'gespraech' | 'hoerbuch';

export interface LiveTalkBillingInfo {
  included_seconds_left?: number;
  coin_per_min?: number;
  coins_per_interval: number;
  interval_seconds: number;
}

// Antwort von POST /api/live-audio/start (siehe Architektur-Spez §2 REST).
export interface StartLiveTalkResponse {
  session_id: number;
  ticket: string;
  ws_path: string;
  billing: LiveTalkBillingInfo;
  balance: number;
  // S9: vom Server gespiegeltes Sitzungsbudget. 0 = kein Limit (Bestand).
  session_coin_limit?: number;
}

// Der Server löst die gespeicherte Stimme auf; pro Anruf gibt es nur Ziel und Stil.
// S9: optionales Sitzungsbudget (Coins). 0/undefined = kein Limit, der Server
// speichert in diesem Fall ebenfalls 0 und sendet die Sitzung ohne Deckel.
export type StartLiveTalkParams =
  | { campaign_id: number; style?: LiveTalkStyle; session_coin_limit?: number }
  | { wizard: true; style?: LiveTalkStyle; session_coin_limit?: number }
  | { character_chat_id: number; session_coin_limit?: number }
  | { table_round_id: number; npc_id?: number; session_coin_limit?: number };

export async function startLiveTalk(params: StartLiveTalkParams): Promise<StartLiveTalkResponse> {
  const body: Record<string, unknown> = {};
  if ('campaign_id' in params) {
    body.campaign_id = params.campaign_id;
    if (params.style) body.style = params.style;
  } else if ('wizard' in params) {
    body.wizard = true;
    if (params.style) body.style = params.style;
  } else if ('table_round_id' in params) {
    body.table_round_id = params.table_round_id;
    if (typeof params.npc_id === 'number') body.npc_id = params.npc_id;
  } else {
    body.character_chat_id = params.character_chat_id;
  }
  // S9: Sitzungsbudget durchreichen — nur senden, wenn der Aufrufer einen
  // Wert hat. 0 wird vom Server als "kein Limit" gewertet, deshalb
  // unterscheiden wir hier nicht (Schnittstelle soll bewusst 0 erlauben).
  const limit = 'session_coin_limit' in params ? params.session_coin_limit : undefined;
  if (typeof limit === 'number' && Number.isFinite(limit) && limit >= 0) {
    body.session_coin_limit = Math.floor(limit);
  }
  return apiRequest<StartLiveTalkResponse>('/api/live-audio/start', {
    method: 'POST',
    body,
  });
}

// Regie-Karten-Warmup (fire-and-forget): beim Öffnen des Preis-Dialogs
// aufrufen, damit die MiniMax-Verdichtung beim WS-Connect schon bereitliegt.
// Fehler werden bewusst geschluckt — das Warmup ist rein optional, der
// Session-Start funktioniert auch ohne (synchroner 3-s-Versuch serverseitig).
// BLEIBT KAMPAGNEN-ONLY — Charakter-Anrufe nutzen andere Warmup-Pfade
// serverseitig und brauchen diesen Hook NICHT.
export async function prepareLiveTalk(campaignId: number): Promise<void> {
  try {
    await apiRequest<unknown>('/api/live-audio/prepare', {
      method: 'POST',
      body: { campaign_id: campaignId },
    });
  } catch {
    /* bewusst geschluckt — Warmup ist optional */
  }
}

// Fallback-Ende (idempotent — der Server räumt hängende Sessions sonst per
// Sweeper auf). keepalive, damit der Call einen Teardown/Seitenwechsel
// überlebt (sendBeacon-Ersatz MIT Auth-Header).
//
// R3-05/D5: `reason` ist der optionale Abbruchgrund des Clients. Der Fehler,
// der einen Anruf kippen lässt (von der CSP blockiertes AudioWorklet,
// verweigertes Mikrofon, nie zustande gekommener WebSocket), wurde bisher im
// Browser verschluckt — im Server-Log stand nichts. Der Server loggt ihn als
// Ereignis + live_audio_errors-Zeile. Ein alter Server ignoriert das Feld.
//
// R3-05/D7-2: dieser Aufruf ist zugleich das Aufräumen der Waisen-Session —
// ohne ihn bliebe die Zeile 'pending' und jeder Zweitversuch liefe bis zu
// 150 s lang in 409 'session_already_active'.
export async function endLiveTalk(sessionId: number, reason?: string): Promise<void> {
  const trimmed = typeof reason === 'string' ? reason.trim().slice(0, 300) : '';
  await apiRequest<unknown>('/api/live-audio/end', {
    method: 'POST',
    body: trimmed ? { session_id: sessionId, reason: trimmed } : { session_id: sessionId },
    keepalive: true,
  });
}

// Fehlercode aus einer Server-Antwort der Form {error:"<code>"} ziehen
// ('live_unavailable' | 'insufficient_coins' | 'session_already_active' |
// 'campaign_not_found' | …). Alles Unbekannte → 'generic'; das Overlay mappt
// Codes auf i18n-Texte und fällt selbst auf live_talk.error_generic zurück.
export function extractLiveErrorCode(error: unknown): string {
  if (error instanceof ApiError && error.data && typeof error.data === 'object') {
    // Ein stabiler Code hat Vorrang vor einer bereits übersetzten Meldung.
    const data = error.data as { code?: unknown; error?: unknown };
    if (typeof data.code === 'string' && data.code) return data.code;
    if (typeof data.error === 'string' && data.error) return data.error;
  }
  return 'generic';
}

const API_BASE = import.meta.env.VITE_API_BASE ?? '';

// API-Basis für Nicht-apiRequest-Konsumenten (z. B. Musik-Player: absolute
// Track-URLs auf Native; leere Base = same-origin/relativ im Web-Build).
export function getLiveApiBase(): string {
  return API_BASE;
}

// WS-URL aus API_BASE ableiten: http→ws, https→wss; leere Base (Web-Build mit
// same-origin-API) → location.origin. BEWUSST ohne Ticket-Query — das Ticket
// (UUID, RFC-6455-token-safe) übergibt der Aufrufer als Subprotocol, damit es
// nie in Access-Logs auftaucht.
export function buildLiveWsUrl(): string {
  const base = (API_BASE || (typeof location !== 'undefined' ? location.origin : ''))
    .replace(/\/+$/, '');
  const wsBase = base.replace(/^http/i, 'ws');
  return `${wsBase}/api/live-audio/ws`;
}
